import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchSurvey, submitSurveyResponse } from './survey.api';
import { matrixRows, minChoices, visibleOptions, visibleQuestions, visibleStatements } from './survey.rules';
import poweredByDqLogo from './PoweredByDQ_white.png';
import './survey.css';

/**
 * SurveyRunner — one survey, start to finish (public, no login, anonymous).
 *
 * Renders any survey definition served by the API (my-dq-api
 * services/survey/definitions/): intro → one question per screen → submit → thank-you.
 * Not tied to any school or organisation. The look is the designer's prototype
 * ("Student AI Literacy Survey - Sample.html"), see survey.css.
 *
 * Question types: single, multi, rank (tap order = rank), matrix (per top-ranked area:
 * pick a statement, rate confidence). Visibility rules live in survey.rules.js.
 *
 * A details form (type `form`, e.g. "About you": name, gender, school, age, country) shows
 * its fields on one screen; each field's own `required` flag decides what must be filled.
 *
 * A `required` question cannot be skipped: as in the assessment player, Next stays
 * disabled (with a note saying what is missing) until it is answered. The API checks
 * the same rule on submit.
 *
 * Progress is kept in localStorage per survey version, so a refresh does not lose
 * answers; it is cleared by "Start a new response".
 *
 * Preview (`previewToken`, from the admin portal's "Preview" button): the unpublished
 * draft, with a banner. Its progress is kept apart from a real response, and Submit
 * goes to the thank-you screen without sending anything.
 */

const storageKey = (code, preview) => `${preview ? 'dq-survey-preview' : 'dq-survey'}:${code}`;

const newSubmissionId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

const freshState = (version) => ({
    version,
    step: 0,
    sel: {},        // { Q1: ['Q1_01'] }
    text: {},       // { 'Q1.other': '…', 'Q1.extra': '…' }
    matrix: {},     // { Q5_07: { s: 'DQC07', c: '3' } }
    form: {},       // { D1: { name: 'Amna', age: '14', country: 'PK' } }
    started_at: null,
    submission_id: newSubmissionId(),
    submitted: false,
});

const loadState = (code, version, preview) => {
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey(code, preview)));
        if (saved && saved.version === version) return saved;
    } catch (e) { /* storage blocked or corrupt: start fresh */ }
    return freshState(version);
};

const saveState = (code, state, preview) => {
    try { localStorage.setItem(storageKey(code, preview), JSON.stringify(state)); } catch (e) { /* ignore */ }
};

const Arrow = ({ back }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d={back ? 'M19 12H5M12 19l-7-7 7-7' : 'M5 12h14M12 5l7 7-7 7'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Check = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TextField = ({ spec, value, onChange }) => (
    <label className="dqs-field">
        <span className="dqs-field-head">
            <span>{spec.label}</span>
            <span className="dqs-field-count">{(value || '').length} / {spec.max_len}</span>
        </span>
        <textarea
            className="dqs-textarea"
            rows={2}
            maxLength={spec.max_len}
            value={value || ''}
            placeholder={spec.placeholder || ''}
            onChange={(e) => onChange(e.target.value.slice(0, spec.max_len))}
        />
    </label>
);

const SurveyRunner = ({ surveyCode, pageTitle, previewToken }) => {
    const preview = Boolean(previewToken);

    const [def, setDef] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [state, setState] = useState(null);
    const [limitFor, setLimitFor] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (!surveyCode) { setLoadError('This survey is not available.'); return; }
        let live = true;
        fetchSurvey(surveyCode, previewToken)
            .then((data) => {
                if (!live) return;
                setDef(data);
                setState(loadState(surveyCode, data.version, preview));
                document.title = `${preview ? 'Preview · ' : ''}${pageTitle || data.title}`;
            })
            .catch((e) => live && setLoadError(e.status === 404
                ? (preview ? 'This preview link has expired. Open a new one from the admin portal.' : 'This survey is not available.')
                : 'This survey could not be loaded. Please try again later.'));
        return () => { live = false; };
    }, [surveyCode, pageTitle, previewToken, preview]);

    const update = useCallback((patch) => {
        setState((prev) => {
            const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
            saveState(surveyCode, next, preview);
            return next;
        });
    }, [surveyCode, preview]);

    const steps = useMemo(() => {
        if (!def || !state) return [];
        return ['intro', ...visibleQuestions(def, state.sel).map((q) => q.id), 'end'];
    }, [def, state]);

    if (loadError) {
        return (
            <div className="dq-survey">
                <header className="dqs-header"><div className="dqs-header-row"><div className="dqs-brand"><img src={poweredByDqLogo} alt="Powered by DQ" className="dqs-brand-logo" /></div></div><div className="dqs-stripe" aria-hidden="true" /></header>
                <main className="dqs-main"><div className="dqs-column"><div className="dqs-card dqs-stack dqs-gap-14"><span className="dqs-kicker">Notice</span><p className="dqs-helper">{loadError}</p></div></div></main>
            </div>
        );
    }
    if (!def || !state) {
        return (
            <div className="dq-survey">
                <header className="dqs-header"><div className="dqs-header-row"><div className="dqs-brand"><img src={poweredByDqLogo} alt="Powered by DQ" className="dqs-brand-logo" /></div></div><div className="dqs-stripe" aria-hidden="true" /></header>
                <main className="dqs-main"><div className="dqs-column"><div className="dqs-card dqs-stack dqs-gap-14"><p className="dqs-helper">Loading survey…</p></div></div></main>
            </div>
        );
    }

    const questionById = (id) => def.questions.find((q) => q.id === id);
    const stepIndex = state.submitted ? steps.length - 1 : Math.min(state.step, steps.length - 2);
    const stepId = steps[stepIndex];
    const question = questionById(stepId);
    const progressPct = `${Math.round((stepIndex / (steps.length - 1)) * 100)}%`;

    const go = (delta) => {
        setLimitFor(null);
        setSubmitError(null);
        update((prev) => ({
            step: Math.max(0, Math.min(steps.length - 2, stepIndex + delta)),
            started_at: prev.started_at || new Date().toISOString(),
        }));
        try { window.scrollTo(0, 0); } catch (e) { /* ignore */ }
    };

    // ── Answer changes ───────────────────────────────────────────────────────

    const toggle = (q, code) => {
        const excl = q.exclusive || [];
        let cur = [...(state.sel[q.id] || [])];
        const has = cur.includes(code);
        let limit = null;
        if (q.type === 'single') cur = has ? [] : [code];
        else if (excl.includes(code)) cur = has ? [] : [code];
        else {
            cur = cur.filter((c) => !excl.includes(c));
            if (has) cur = cur.filter((c) => c !== code);
            else if (q.max && cur.length >= q.max) limit = q.id;
            else cur.push(code);
            // Ranking every option: the last one is placed for you.
            const opts = visibleOptions(q, state.sel).map((o) => o.code);
            if (q.type === 'rank' && !has && q.max >= opts.length && cur.length === opts.length - 1) {
                cur.push(opts.find((c) => !cur.includes(c)));
            }
        }
        setLimitFor(limit);
        update((prev) => ({ sel: { ...prev.sel, [q.id]: cur } }));
    };

    const setText = (q, kind, value) => {
        update((prev) => {
            const patch = { text: { ...prev.text, [`${q.id}.${kind}`]: value } };
            // Typing an "other" answer ticks the Other option, room permitting.
            if (kind === 'other' && value && q.other && q.other.always_show) {
                const cur = prev.sel[q.id] || [];
                if (!cur.includes(q.other.code)) {
                    const base = cur.filter((c) => !(q.exclusive || []).includes(c));
                    if (q.type === 'single') patch.sel = { ...prev.sel, [q.id]: [q.other.code] };
                    else if (!q.max || base.length < q.max) patch.sel = { ...prev.sel, [q.id]: [...base, q.other.code] };
                }
            }
            return patch;
        });
    };

    const setMatrix = (row, field, value) => {
        update((prev) => {
            const cur = { ...(prev.matrix[row] || {}) };
            cur[field] = cur[field] === value ? null : value;
            return { matrix: { ...prev.matrix, [row]: cur } };
        });
    };

    const setFormValue = (q, key, value) => {
        update((prev) => {
            const forms = prev.form || {};
            return { form: { ...forms, [q.id]: { ...(forms[q.id] || {}), [key]: value } } };
        });
    };

    const formValues = (q) => ((state.form || {})[q.id]) || {};

    // A details-form field: 'empty', 'ok' or 'invalid' (the API applies the same rules).
    const fieldState = (field, raw) => {
        const v = typeof raw === 'string' ? raw.trim() : raw;
        if (v === undefined || v === null || v === '') return 'empty';
        if (field.input === 'number') {
            const n = Number(v);
            const ok = Number.isInteger(n) && (field.min === undefined || n >= field.min) && (field.max === undefined || n <= field.max);
            return ok ? 'ok' : 'invalid';
        }
        if (field.input === 'choice' || field.input === 'select') return (field.options || []).some((o) => o.code === v) ? 'ok' : 'invalid';
        return 'ok';
    };

    const answered = (q) => {
        if (q.type === 'form') return (q.fields || []).some((fd) => fieldState(fd, formValues(q)[fd.key]) === 'ok');
        if (q.type === 'matrix') return matrixRows(q, state.sel).some((r) => (state.matrix[r.code] || {}).s || (state.matrix[r.code] || {}).c);
        return (state.sel[q.id] || []).length > 0;
    };

    // Answered well enough to move on (see minChoices in survey.rules.js).
    const complete = (q) => {
        if (q.type === 'form') {
            return (q.fields || []).every((fd) => {
                const st = fieldState(fd, formValues(q)[fd.key]);
                return st === 'ok' || (st === 'empty' && !fd.required);
            });
        }
        if (q.type === 'matrix') {
            return matrixRows(q, state.sel).every((r) => {
                const m = state.matrix[r.code] || {};
                const statementChosen = m.s || visibleStatements(r, state.sel).length === 1;
                return statementChosen && m.c;
            });
        }
        const visible = visibleOptions(q, state.sel).map((o) => o.code);
        const codes = (state.sel[q.id] || []).filter((c) => visible.includes(c));
        if (codes.some((c) => (q.exclusive || []).includes(c))) return true;
        return codes.length >= minChoices(q, visible.length);
    };

    // A form is checked field by field (each has its own `required`); other questions as a whole.
    const blocked = (q) => (q.type === 'form' ? !complete(q) : q.required && !complete(q));

    // What is still missing, under a disabled Next.
    const requiredNote = (q) => {
        if (q.required_note) return q.required_note;
        if (q.type === 'form') return 'Fill in the required fields to continue.';
        if (q.type === 'matrix') {
            const single = (q.rows || []).length && visibleStatements(q.rows[0], state.sel).length === 1;
            return single
                ? 'Rate your confidence for each area to continue.'
                : 'For each area, choose a statement and rate your confidence to continue.';
        }
        if (q.type === 'single') return 'Choose an option to continue.';
        const total = visibleOptions(q, state.sel).length;
        const min = minChoices(q, total);
        if (q.type === 'rank' && min === total) return `Rank all ${total} to continue.`;
        return min > 1 ? `Choose at least ${min} to continue.` : 'Choose at least one option to continue.';
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const buildAnswers = () => {
        const out = [];
        visibleQuestions(def, state.sel).forEach((q) => {
            if (q.type === 'form') {
                const values = {};
                (q.fields || []).forEach((fd) => {
                    const raw = formValues(q)[fd.key];
                    if (fieldState(fd, raw) !== 'ok') return;
                    values[fd.key] = fd.input === 'number' ? Number(raw) : (typeof raw === 'string' ? raw.trim() : raw);
                });
                if (Object.keys(values).length) out.push({ q: q.id, values });
                return;
            }
            if (q.type === 'matrix') {
                matrixRows(q, state.sel).forEach((row) => {
                    const m = state.matrix[row.code] || {};
                    if (!m.s && !m.c) return;
                    out.push({ q: q.id, row: row.code, codes: m.s ? [m.s] : [], scale: m.c || undefined });
                });
                return;
            }
            const visible = visibleOptions(q, state.sel).map((o) => o.code);
            const codes = (state.sel[q.id] || []).filter((c) => visible.includes(c));
            const showOther = q.other && (q.other.always_show || codes.includes(q.other.code));
            const other_text = showOther ? (state.text[`${q.id}.other`] || '').trim() : '';
            const extra_text = q.extra ? (state.text[`${q.id}.extra`] || '').trim() : '';
            if (!codes.length && !other_text && !extra_text) return;
            out.push({ q: q.id, codes, other_text: other_text || undefined, extra_text: extra_text || undefined });
        });
        return out;
    };

    const submit = async () => {
        // Every step is checked on the way, but an earlier answer may have changed since
        // (e.g. a new top-three in Q5 brings new areas into Q6): go back to the first gap.
        const gap = visibleQuestions(def, state.sel).find(blocked);
        if (gap) {
            update({ step: steps.indexOf(gap.id) });
            setSubmitError(requiredNote(gap));
            return;
        }
        const answers = buildAnswers();
        if (!answers.length) { setSubmitError('Please answer at least one question before submitting.'); return; }
        if (preview) {
            // A draft cannot take responses: show the thank-you screen, send nothing.
            update({ submitted: true });
            try { window.scrollTo(0, 0); } catch (e) { /* ignore */ }
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            await submitSurveyResponse({
                survey_code: def.survey_code,
                survey_version: def.version,
                client_submission_id: state.submission_id,
                answers,
                started_at: state.started_at,
                locale: navigator.language,
            });
            update({ submitted: true });
            try { window.scrollTo(0, 0); } catch (e) { /* ignore */ }
        } catch (e) {
            setSubmitError(e.message || 'Your answers could not be sent. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const restart = () => {
        setLimitFor(null);
        setSubmitError(null);
        update(freshState(def.version));
    };

    // ── Screens ──────────────────────────────────────────────────────────────

    const header = (label) => (
        <header className="dqs-header">
            <div className="dqs-header-row">
                <div className="dqs-brand">
                    <img src={poweredByDqLogo} alt="Powered by DQ" className="dqs-brand-logo" />
                    {(pageTitle || def.title) && (
                        <>
                            <span className="dqs-brand-divider" aria-hidden="true" />
                            <span className="dqs-brand-title">{pageTitle || def.title}</span>
                        </>
                    )}
                </div>
                <div className="dqs-step-label">{label}</div>
            </div>
            <div className="dqs-stripe" aria-hidden="true" />
            <div className="dqs-progress"><div className="dqs-progress-bar" style={{ width: progressPct }} /></div>
        </header>
    );

    const renderIntro = () => (
        <div className="dqs-card dqs-stack dqs-gap-32">
            <h1 className="dqs-h1">{def.intro.title}</h1>
            <div className="dqs-intro-body">
                {(def.intro.paragraphs || []).map((p) => <p key={p}>{p}</p>)}
            </div>
            {!!(def.intro.stats || []).length && (
                <div className="dqs-stats">
                    {def.intro.stats.map((s) => (
                        <div className="dqs-stat" key={s.label}>
                            <div className="dqs-stat-value">{s.value}</div>
                            <div className="dqs-stat-label">{s.label}</div>
                        </div>
                    ))}
                </div>
            )}
            <div className="dqs-intro-actions">
                <button type="button" className="dqs-btn dqs-btn-primary dqs-btn-wide" onClick={() => go(1)}>
                    {def.intro.start_label || 'Start'} <Arrow />
                </button>
            </div>
        </div>
    );

    // Thank-you screen with a promo card (teacher survey): kicker + title on white, then the
    // Powered by DQ card. The link must be http(s); anything else drops the button.
    const renderEndWithPromo = (promo) => {
        const href = /^https?:\/\//i.test(promo.cta_url || '') ? promo.cta_url : null;
        return (
            <div className="dqs-promo-card">
                <div className="dqs-promo-top">
                    {def.end.kicker && <span className="dqs-kicker">{def.end.kicker}</span>}
                    <div className="dqs-promo-title">{def.end.title}</div>
                    {def.end.body && <p className="dqs-helper">{def.end.body}</p>}
                </div>
                <div className="dqs-promo-bottom">
                    <img src={poweredByDqLogo} alt="Powered by DQ" className="dqs-promo-logo" />
                    <div className="dqs-promo-heading">{promo.heading}</div>
                    {href && (
                        <a className="dqs-btn dqs-btn-primary dqs-promo-cta" href={href} target="_blank" rel="noopener noreferrer">
                            {promo.cta_label}<Arrow />
                        </a>
                    )}
                </div>
            </div>
        );
    };

    const renderEnd = () => (def.end.promo ? renderEndWithPromo(def.end.promo) : (
        <div className="dqs-stack dqs-gap-32">
            <div className="dqs-end-card">
                <div className="dqs-end-badge">
                    <div className="dqs-end-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div className="dqs-end-kicker">{def.end.kicker}</div>
                </div>
                <div className="dqs-end-title">{def.end.title}</div>
                <div className="dqs-end-body">{def.end.body}</div>
            </div>
            <div className="dqs-end-actions">
                <button type="button" className="dqs-btn dqs-btn-primary dqs-btn-wide" onClick={restart}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                        <path d="M21 3v5h-5" />
                        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                        <path d="M3 21v-5h5" />
                    </svg>
                    <span>Start a new response</span>
                </button>
            </div>
        </div>
    ));

    const renderOptions = (q) => {
        const sel = state.sel[q.id] || [];
        const isRank = q.type === 'rank';
        const isSingle = q.type === 'single';
        const full = q.max && q.type !== 'single' && sel.length >= q.max;
        return (
            <div className="dqs-options">
                {visibleOptions(q, state.sel).map((o) => {
                    const i = sel.indexOf(o.code);
                    const selected = i >= 0;
                    const cls = ['dqs-option', selected && 'is-selected', full && !selected && 'is-dimmed'].filter(Boolean).join(' ');
                    return (
                        <button type="button" key={o.code} className={cls} aria-pressed={selected} onClick={() => toggle(q, o.code)}>
                            <span className={`dqs-mark${isSingle ? ' is-radio' : ''}`}>
                                {selected && (isRank ? <span>{i + 1}</span> : (isSingle ? <span className="dqs-radio-dot" /> : <Check />))}
                            </span>
                            <span className="dqs-option-text">
                                <span className={`dqs-option-label${q.emphasis ? ' is-emphasis' : ''}`}>{o.label}</span>
                                {o.tagline && <span className="dqs-option-tagline">{o.tagline}</span>}
                                {o.description && <span className="dqs-option-desc">{o.description}</span>}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderRankTools = (q) => {
        const sel = state.sel[q.id] || [];
        if (q.type !== 'rank' || !sel.length) return null;
        const excl = sel.find((c) => (q.exclusive || []).includes(c));
        const total = visibleOptions(q, state.sel).length;
        let status = `${sel.length} of ${q.max} chosen`;
        if (excl) status = (q.options.find((o) => o.code === excl) || {}).label;
        else if (q.max >= total) status = `${sel.length} of ${total} ranked`;
        return (
            <div className="dqs-rank-tools">
                <div className="dqs-rank-status">{status}</div>
                <button type="button" className="dqs-btn dqs-btn-ghost" onClick={() => update((prev) => ({ sel: { ...prev.sel, [q.id]: [] } }))}>
                    Clear ranking
                </button>
            </div>
        );
    };

    const renderMatrix = (q) => {
        const rows = matrixRows(q, state.sel);
        return (
            <div className="dqs-matrix">
                {!rows.length && <div className="dqs-note">{q.empty_note}</div>}
                {rows.map((row, i) => {
                    const m = state.matrix[row.code] || {};
                    const statements = visibleStatements(row, state.sel);
                    const single = statements.length === 1;
                    return (
                        <div className="dqs-area" key={row.code}>
                            <div className="dqs-area-head">
                                <span className="dqs-area-n">{i + 1}</span>
                                <div>
                                    <span className="dqs-area-name">{row.label}</span>
                                    <span className="dqs-area-tagline">{row.tagline}</span>
                                </div>
                            </div>
                            <div className="dqs-label-caps">{single ? q.statement_prompt_single : q.statement_prompt}</div>
                            <div className="dqs-statements">
                                {statements.map((s) => {
                                    const selected = m.s === s.code;
                                    return (
                                        <button
                                            type="button"
                                            key={s.code}
                                            className={`dqs-option dqs-statement${selected ? ' is-selected' : ''}`}
                                            aria-pressed={selected}
                                            onClick={() => setMatrix(row.code, 's', s.code)}
                                        >
                                            <span className="dqs-mark is-radio">{selected && <span className="dqs-radio-dot" />}</span>
                                            <span className="dqs-statement-label">{s.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className={`dqs-scale-wrap${m.s || single ? '' : ' is-waiting'}`}>
                                <div className="dqs-label-caps">{q.scale_prompt}</div>
                                <div className="dqs-scale">
                                    {q.scale.map((c) => {
                                        const selected = m.c === c.code;
                                        return (
                                            <button
                                                type="button"
                                                key={c.code}
                                                className={`dqs-scale-cell${selected ? ' is-selected' : ''}`}
                                                aria-pressed={selected}
                                                onClick={() => setMatrix(row.code, 'c', c.code)}
                                            >
                                                <span className="dqs-scale-num">{c.code === 'NT' ? '—' : c.code}</span>
                                                <span className="dqs-scale-label">{c.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderForm = (q) => (
        <div className="dqs-form">
            {(q.fields || []).map((fd) => {
                const value = formValues(q)[fd.key];
                const st = fieldState(fd, value);
                const id = `dqs-${q.id}-${fd.key}`;
                const label = (
                    <span className="dqs-field-head">
                        <span>{fd.label}{!fd.required && <span className="dqs-field-optional"> (optional)</span>}</span>
                        {fd.input === 'text' && fd.max_len && <span className="dqs-field-count">{(value || '').length} / {fd.max_len}</span>}
                    </span>
                );
                if (fd.input === 'choice') {
                    return (
                        <div className="dqs-field" key={fd.key} role="radiogroup" aria-label={fd.label}>
                            {label}
                            <div className="dqs-pills">
                                {(fd.options || []).map((o) => (
                                    <button
                                        type="button"
                                        key={o.code}
                                        className={`dqs-pill${value === o.code ? ' is-selected' : ''}`}
                                        aria-pressed={value === o.code}
                                        onClick={() => setFormValue(q, fd.key, value === o.code ? '' : o.code)}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                }
                return (
                    <label className="dqs-field" key={fd.key} htmlFor={id}>
                        {label}
                        {fd.input === 'select' ? (
                            <select id={id} className="dqs-input dqs-select" value={value || ''} onChange={(e) => setFormValue(q, fd.key, e.target.value)}>
                                <option value="">{fd.placeholder || 'Choose…'}</option>
                                {(fd.options || []).map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                            </select>
                        ) : (
                            <input
                                id={id}
                                className="dqs-input"
                                type={fd.input === 'number' ? 'number' : 'text'}
                                inputMode={fd.input === 'number' ? 'numeric' : undefined}
                                min={fd.min}
                                max={fd.max}
                                maxLength={fd.input === 'text' ? fd.max_len : undefined}
                                placeholder={fd.placeholder || ''}
                                value={value === undefined || value === null ? '' : value}
                                onChange={(e) => setFormValue(q, fd.key, fd.input === 'text' ? e.target.value.slice(0, fd.max_len || 200) : e.target.value)}
                            />
                        )}
                        {st === 'invalid' && fd.input === 'number' && (
                            <span className="dqs-field-error">Enter a valid {(fd.label || 'number').toLowerCase()}.</span>
                        )}
                    </label>
                );
            })}
        </div>
    );

    const renderQuestion = (q) => {
        const sel = state.sel[q.id] || [];
        const isLast = steps[stepIndex + 1] === 'end';
        let helper = q.helper;
        if (q.type === 'matrix' && q.helper_single && q.rows && q.rows.length) {
            helper = visibleStatements(q.rows[0], state.sel).length > 1 ? q.helper : q.helper_single;
        }
        // e.g. Q4 repeats the concern ranked first in Q3.
        let context = null;
        if (q.context) {
            const first = (state.sel[q.context.q] || [])[0];
            const opt = ((questionById(q.context.q) || {}).options || []).find((o) => o.code === first);
            if (opt) context = { label: q.context.label, text: opt.label };
        }
        const showOther = q.other && (q.other.always_show || sel.includes(q.other.code));
        const nextLabel = q.required
            ? (isLast ? 'Submit' : 'Next')
            : (isLast ? (answered(q) ? 'Submit' : 'Skip and submit') : (answered(q) ? 'Next' : 'Skip'));
        const isBlocked = blocked(q);

        return (
            <div className="dqs-card dqs-stack dqs-gap-28">
                <div className="dqs-stack dqs-gap-14">
                    <div className="dqs-kicker">{q.kicker}</div>
                    <h2 className="dqs-h2">{q.title}</h2>
                    {helper && <p className="dqs-helper">{helper}</p>}
                </div>

                {context && (
                    <div className="dqs-context">
                        <div className="dqs-context-label">{context.label}</div>
                        <div className="dqs-context-text">{context.text}</div>
                    </div>
                )}

                {q.type === 'form' ? renderForm(q) : (q.type === 'matrix' ? renderMatrix(q) : renderOptions(q))}
                {renderRankTools(q)}
                {limitFor === q.id && q.limit_note && <div className="dqs-limit-note">{q.limit_note}</div>}

                {showOther && (
                    <TextField spec={q.other} value={state.text[`${q.id}.other`]} onChange={(v) => setText(q, 'other', v)} />
                )}
                {q.extra && (
                    <TextField spec={q.extra} value={state.text[`${q.id}.extra`]} onChange={(v) => setText(q, 'extra', v)} />
                )}

                {submitError && <div className="dqs-error" role="alert">{submitError}</div>}
                {isBlocked && !submitError && <div className="dqs-required-note">{requiredNote(q)}</div>}

                <div className="dqs-nav">
                    <button type="button" className="dqs-btn dqs-btn-secondary" onClick={() => go(-1)} disabled={submitting}>
                        <Arrow back />Back
                    </button>
                    <button
                        type="button"
                        className="dqs-btn dqs-btn-primary"
                        onClick={isLast ? submit : () => go(1)}
                        disabled={submitting || isBlocked}
                    >
                        {submitting ? 'Sending…' : nextLabel}<Arrow />
                    </button>
                </div>
            </div>
        );
    };

    let label = 'Introduction';
    let body = renderIntro();
    if (stepId === 'end') { label = 'Complete'; body = renderEnd(); }
    else if (question) { label = question.kicker; body = renderQuestion(question); }

    return (
        <div className="dq-survey">
            {header(label)}
            {preview && (
                <div className="dqs-preview-banner" role="status">
                    Preview of draft v{def.version} — not published. Answers are not saved.
                </div>
            )}
            <main className="dqs-main">
                <div className="dqs-column">{body}</div>
            </main>
        </div>
    );
};

export default SurveyRunner;
